import $ from "jquery";
import { localLogout } from "../actions/userActions";
import NetworkService from "../services/networkService";

const TIMEOUT = 10000;
const USE_CACHE = false;

/**
 * The class containing and handling all api calls
 */
class MmxApi {
  /**
   * Creates an instance of the class.
   *
   * @param {String}  server          The api for the server. This will be added before all api calls.
   * @param {String}  securityToken   The security token for the session.
   * @param {Function}  dispatch      Used to send actions to redux.
   */
  constructor(server, securityToken, dispatch) {
    this.server = server;
    this.securityToken = securityToken;
    this.dispatch = dispatch;
    this.serverError = false;
  }

  /* These are what is used outside of the class */
  contacts = {
    getPersistantContacts: searchString => {
      return this.get("contacts/getPersistantContacts");
    },
    search: searchString => {
      return this.get("contacts/search", { searchString: searchString });
    },
    delete: id => {
      return this.get("contacts/delete", { phonebookId: id }, false);
    },
    update: payload => {
      return this.post("contacts/update", payload);
    },
    add: payload => {
      return this.post("contacts/add", payload);
    },
  };
  messages = {
    messages: () => {
      return this.get("messages");
    },
    markAsRead: messageNumber => {
      return this.put(`messages/${messageNumber}/markAsRead`, true);
    },
    delete: messageNumber => {
      return this.delete(`messages/${messageNumber}`, "DELETE");
    },
    deleteReadMessages: () => {
      return this.post("messages/deleteReadMessages");
    },
  };
  user = {
    systemSettings: () => {
      return this.get("user/getSystemSettingsForUser");
    },
    validateByKey: (key, value) => {
      // TODO: change api to GET on server side
      return this.post("signUp/validateByKey", {}, { key: key, value: value });
    },
    getUserProperties: () => {
      return this.get("user/getProperties");
    },
    setUserProperty: (key, value) => {
      // TODO: change api to POST on server side
      return this.get("user/setProperty", { key: key, value: value }, false);
    },
    login: payload => {
      return new Promise((resolve, reject) => {
        // Try to login via POST request
        this.post("user/login", payload, {}, true, "json")
          .done(data => {
            resolve(data);
          })
          .fail((xhr, ajaxOptions, thrownError) => {
            // POST method is implemented, login failed for legit reasons
            if (xhr.status !== 405) {
              reject({ xhr, ajaxOptions, thrownError });
              return;
            }

            // POST method is NOT implemented, try logging in via GET request
            this.get("user/login", payload, true, "json")
              .done(data => {
                resolve(data);
              })
              .fail((xhr, ajaxOptions, thrownError) => {
                reject({ xhr, ajaxOptions, thrownError });
              });
          });
      });
    },
    logout: () => {
      // TODO: change api to POST on server side
      return this.get("user/logout", {}, false);
    },
    createUser: (payload, params) => {
      return this.post("signUp/create", payload, params, true);
    },
    getOrgSettings: params => {
      return this.get("user/getOrgSettings", params);
    },
    requestResetPassword: params => {
      return this.get("user/requestResetPassword", params, false);
    },
    setNewPassword: params => {
      // TODO: change api to POST on server side
      return this.get("user/setNewPassword", params, false);
    },
  };
  settings = {
    webSettings: () => {
      return this.get("user/webSettings");
    },
    updateSetting: (key, payload) => {
      return this.post("user/webSettings/" + key, payload);
    },
  };
  sip = {
    updateRegisteredEndpoint: params => {
      return this.get("user/updateRegisteredEndpoint", params, false);
    },
  };

  /* Help functions to abstract some parameters to the function sendJson. */
  get(apiAction, params = {}, expectedRespons = true) {
    return this.sendJson(apiAction, "GET", params, {}, expectedRespons);
  }
  post(apiAction, payload = {}, params = {}, expectedRespons = false) {
    return this.sendJson(apiAction, "POST", params, payload, expectedRespons);
  }
  put(apiAction, expectedRespons = false) {
    return this.sendJson(apiAction, "PUT", {}, {}, expectedRespons);
  }
  delete(apiAction) {
    return this.sendJson(apiAction, "DELETE", {}, {}, false);
  }

  /**
   * Does an api call to the server for the given api action and http method.
   *
   * @param {String} apiAction            The action the user wants from the server, for example:
   *                                      "user/login", "contacts/add".
   * @param {String}  httpMethod          The http method to be used, for example: 'PUT', 'DELETE'.
   * @param {Object}  params              An object with parameters to be added to the url.
   * @param {Object}  payload             An object with the payload to be sent to server.
   * @param {Boolean}  expectedRespons    Is a respons expected from the server? If not then the data type of
   * @param {String}  dataType            Expected data type for the reponse.
   * @return {String}                     The response from the server. If no contact with the server was made then
   *                                      the function "handleAjaxError" will be run abrupting this one.
   */
  sendJson(
    apiAction,
    httpMethod,
    params = {},
    payload = {},
    expectedRespons = true,
    dataType = "html"
  ) {
    if (apiAction === null || httpMethod === null) {
      return null;
    }

    var jsonObject = {
      method: httpMethod,
      url: this.addParams(this.url(apiAction), params),
      timeout: TIMEOUT,
      cache: USE_CACHE,
      dataType: "json",
    };

    if (httpMethod.toUpperCase() === "POST") {
      jsonObject.crossDomain = true;
      jsonObject.contentType = "application/json; charset=utf-8";
      if (typeof payload === "object") {
        jsonObject.data = JSON.stringify(payload);
      } else {
        jsonObject.data = payload;
      }
    }

    if (expectedRespons === false) {
      jsonObject = {
        ...jsonObject,
        dataType,
      };
    }

    var response = $.ajax(jsonObject);
    response
      .done(() => {
        NetworkService.clearServerErrorAlert(this.dispatch);
      })
      .fail((xhr, ajaxOptions, thrownError) => {
        console.error(
          "Error when sending action request to server: ",
          apiAction
        );
        if (thrownError === "Unauthorized") {
          this.dispatch(localLogout());
        } else if (xhr.status === 0) {
          NetworkService.showErrorAlert(this.dispatch, "serverError");
        }
      });

    return response;
  }

  /**
   * Creates the url without parameters. The purpose of this function is to abstract the api destination
   * so it will only be written in one place.
   *
   * @param {String}  method      The part of the url that changes depending on the method/action the user
   *                              wants from the server, for example: "user/login", "contacts/add".
   * @return {String}             The concated string of the url and parameters.
   */
  url(method) {
    return `${this.server}/userApi/user/api/${method}`;
  }

  /**
   * This function adds the params to the url with a separation of a ? between.
   *
   * @param {String}  url         An url.
   * @param {Object}  params      An object with keys/fields containing the parameters to be added.
   * @return {String}             The concated string of the url and parameters.
   */
  addParams(url, params) {
    var newUrl = "";
    if (this.securityToken !== null && this.securityToken !== undefined) {
      params.securityToken = this.securityToken;
    }
    if (params != null && Object.keys(params).length > 0) {
      const paramStr = Object.keys(params)
        .map(key => {
          return key + "=" + params[key];
        })
        .join("&");
      newUrl = `${url}?${paramStr}`;
    } else {
      newUrl = url;
    }
    return newUrl;
  }
}

export default MmxApi;






// WEBPACK FOOTER //
// ./src/api/mmxApi.js